import {
  ContentContainer,
  PageContainer,
} from 'fitify-ui-onboarding/src/components'
import { useUserContext } from 'fitify-ui-onboarding/src/contexts/UserContext'
import { getPaging } from 'fitify-ui-onboarding/src/hooks/useNavigation'
import { encodeQuery, setItem } from 'fitify-ui-onboarding/src/utils'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useContext, useEffect, useState } from 'react'

import { createUserProfile } from 'api/userProfile'
import { Layout } from 'components/Layout'
import { PageTitle } from 'components/PageTitle'
import { CoachLoading } from 'components/Plan/CoachLoading'
import { fetchCoaches } from 'hooks/useCoaches'
import { LOGGED_IN_STORAGE_COACH_KEY, STORAGE_COACH_KEY } from 'utils/constants'
import { getStaticPropsTranslations } from 'utils/i18n'
import { APP_PAGES, ONBOARDING_PAGES, ONBOARDING_PAGES_MAP } from 'utils/routes'
import { DataContext } from 'utils/state'

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await getStaticPropsTranslations(locale)),
    },
  }
}

export default function Page() {
  const { t } = useTranslation()
  const router = useRouter()
  const { data } = useContext(DataContext)

  const [isDataLoading, setIsDataLoading] = useState<boolean>(true)
  const [isFetchingCoaches, setIsFetchingCoaches] = useState<boolean>(true)

  const { isLoggedIn } = useUserContext()

  useEffect(() => {
    if (!isLoggedIn && isFetchingCoaches) return

    // Check if all the data is available
    const onboardingKeys = Object.values(ONBOARDING_PAGES_MAP)
      .filter((page) => page.required)
      .map((page) => page.key)
    const dataKeys = Object.keys(data)
    const isDataValid = onboardingKeys.every(
      (value) => value && dataKeys.includes(value)
    )

    const fetchData = async () => {
      if (isDataValid) {
        await createUserProfile(data)
      }

      const coachesData = await fetchCoaches()
      if (coachesData && coachesData.length > 0) {
        setItem(
          'session',
          isLoggedIn ? LOGGED_IN_STORAGE_COACH_KEY : STORAGE_COACH_KEY,
          JSON.stringify(coachesData)
        )
      }

      setIsFetchingCoaches(false)
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  useEffect(() => {
    const paging = getPaging({ path: router.pathname, data })

    if (paging.next && !isDataLoading && !isFetchingCoaches) {
      router.replace(paging.next.path)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataLoading, isFetchingCoaches])

  const onComplete = () => {
    if (!isLoggedIn) {
      router.push(
        encodeQuery(APP_PAGES.signup.path, {
          next: encodeQuery(ONBOARDING_PAGES.loadingCoaches.path),
        })
      )
    } else {
      setIsDataLoading(false)
    }
  }

  return (
    <Layout
      options={{
        isPrevHidden: true,
      }}
    >
      <PageTitle
        title={t('hc_onboarding_generator_progress_description')}
        noIndex
      />
      <PageContainer>
        <ContentContainer align="center" justify="center">
          <CoachLoading onComplete={onComplete} />
        </ContentContainer>
      </PageContainer>
    </Layout>
  )
}
